import { CurrentBrand, TikTokSparkAd, TikTokTargetingGeography } from "modules/Types";
import { TikTokAdAccount } from "modules/Types";
import * as Sentry from "@sentry/react";
import Axios from "helpers/Interceptor";
/**
 * Retrieves TikTok ad accounts for a brand
 * @param brandId - ID of the brand to fetch ad accounts for
 * @returns Promise containing the TikTok ad accounts or error
 */
export const getTikTokAdAccounts_api = async (
    brandId: CurrentBrand['id']
): Promise<TikTokAdAccount[]> => {
    try {
        const response = await Axios.get<TikTokAdAccount[]>(`/tiktok/${brandId}/ad-accounts`);
        return response.data ?? [];
    } catch (error) {
        console.error('Error fetching TikTok ad accounts:', error);
        Sentry.captureException(error);
        return [];
    }
};


/** Interface for TikTok creative authorization request */
interface TikTokCreativeAuthRequest {
    /** TikTok Spark code for video authorization */
    tiktokSparkCode: string;
    /** Whether the creative is being authorized for express setup */
    isExpressSetup: boolean;
}


/**
* Authorizes a TikTok creative using a spark code for a specific brand
* @param brandId - ID of the brand to authorize the creative for
* @param params - Request parameters including the TikTok spark code
* @returns Promise containing the authorized creative details
*/
export const authorizeTikTokCreative_api = async (
    brandId: CurrentBrand['id'],
    params: TikTokCreativeAuthRequest
): Promise<TikTokSparkAd> => {
    try {
        const response = await Axios.post<TikTokSparkAd>(
            `/tiktok/${brandId}/creatives/authorize`,
            params
        );

        if (!response || response.status !== 200) {
            throw new Error('Error authorizing TikTok creative');
        }

        return response.data;
    } catch (error) {
        console.error('Error authorizing TikTok creative:', error);
        Sentry.captureException(error);
        throw error;
    }
};


/** Interface for TikTok ad account creation parameters */
export interface CreateTikTokAdAccountParams {
    /** ID of the business center */
    business_center_id: string;
    /** Full name of the account owner */
    full_name: string;
    /** Phone number for the account */
    phone_number: string;
    /** Email address for the account */
    email: string;
    /** Name of the ad account */
    ad_account_name: string;
    /** Country code for the account */
    country_code: string;
  }
  
  /**
   * Creates a new TikTok ad account for a brand
   * @param brandId - ID of the brand to create the ad account for
   * @param params - Parameters for creating the ad account
   * @returns Promise containing the created ad account or error
   */
  export const createTikTokAdAccount_api = async (
    brandId: CurrentBrand['id'],
    params: CreateTikTokAdAccountParams
  ): Promise<{ data: TikTokAdAccount | null; error: unknown | null }> => {
    try {
      const response = await Axios.post<TikTokAdAccount>(
        `/tiktok/${brandId}/ad-accounts`,
        params
      );
      return { data: response.data, error: null };
    } catch (error) {
      console.error('Error creating TikTok ad account:', error);
      Sentry.captureException(error);
      return { data: null, error };
    }
  };

/**
 * Retrieves a single TikTok ad account by ID
 * @param brandId - ID of the brand
 * @param accountId - ID of the TikTok ad account
 */
export const getConnectedTikTokAdAccount_api = async (
  brandId: CurrentBrand['id'],
  accountId: string
): Promise<any | null> => {
  try {
    const response = await Axios.get<any>(
      `/tiktok/${brandId}/ad-accounts/${accountId}`
    );
    return response.data ?? null;
  } catch (error) {
    console.error('Error fetching TikTok ad account:', error);
    Sentry.captureException(error);
    return null;
  }
};

/** Interface for location targeting response */
interface TargetingLocation {
  id: string;
  name: string;
  type: string;
}

/**
 * Retrieves available targeting locations for TikTok ads
 * @param brandId - ID of the brand
 * @param keyword - Optional search term to filter locations
 */
export const getAvailableTargetingLocations_api = async (
  brandId: CurrentBrand['id'],
  keyword?: string
): Promise<TikTokTargetingGeography[] | null> => {
  try {
    const response = await Axios.get<TikTokTargetingGeography[]>(
      `/tiktok/${brandId}/targeting/locations`,
      { params: { keyword } }
    );
    return response.data ?? null;
  } catch (error) {
    console.error('Error fetching targeting locations:', error);
    Sentry.captureException(error);
    return null;
  }
};

/** Interface for targeting interests response */
interface TargetingInterest {
  id: string;
  name: string;
  category: string;
}

/** Interface for TikTok hashtag targeting item */
interface TikTokHashtagItem {
  id: string;
  name: string;
  targetingType: string;
  hashtagType: string;
}

/** Interface for TikTok creator targeting item */
interface TikTokCreatorItem {
  id: string;
  name: string;
  targetingType: string;
}

/** Interface for TikTok video interaction targeting item */
interface TikTokVideoInteractionItem {
  id: string;
  name: string;
  targetingType: string;
}

/** Interface for TikTok purchase intention targeting item */
interface TikTokPurchaseIntentionItem {
  id: string;
  name: string;
  targetingType: string;
}

/** Interface representing the complete TikTok targeting response structure */
interface TikTokTargetingResponse {
  /** List of hashtag targeting options */
  hashtags: TikTokHashtagItem[];
  /** List of interest targeting options */
  interests: TargetingInterest[];
  /** List of creator targeting options */
  creators: TikTokCreatorItem[];
  /** List of video interaction targeting options */
  videoInteractions: TikTokVideoInteractionItem[];
  /** List of purchase intention targeting options */
  purchaseIntentions: TikTokPurchaseIntentionItem[];
}

/**
 * Searches for TikTok targeting interests
 * @param brandId - ID of the brand
 * @param query - Search query string
 * @param isExpressSetup - Whether this is for express setup
 */
export const searchTiktokTargetingInterests_api = async (
  brandId: CurrentBrand['id'],
  query: string,
  isExpressSetup?: boolean
): Promise<{ data: TikTokTargetingResponse | null; error: unknown | null }> => {
  try {
    const response = await Axios.post<TikTokTargetingResponse>(
      `/tiktok/${brandId}/targeting/interests`,
      { query, isExpressSetup }
    );
    return { data: response.data, error: null };
  } catch (error) {
    console.error('Error searching targeting interests:', error);
    Sentry.captureException(error);
    return { data: null, error };
  }
};

/** Interface for recently used spark codes */
interface SparkCode {
  code: string;
  createdAt: string;
  videoDetails: {
    title: string;
    thumbnailUrl: string;
  };
}

/**
 * Retrieves recently used TikTok spark codes
 * @param brandId - ID of the brand
 */
export const getTikTokRecentlyUsedSparkCodes_api = async (
  brandId: CurrentBrand['id']
): Promise<{ data: SparkCode[] | null; error: unknown | null }> => {
  try {
    const response = await Axios.get<SparkCode[]>(
      `/tiktok/${brandId}/creatives/recent`
    );
    return { data: response.data, error: null };
  } catch (error) {
    console.error('Error fetching recent spark codes:', error);
    Sentry.captureException(error);
    return { data: null, error };
  }
};

/** Interface for targeting recommendations */
interface TargetingRecommendation {
  id: string;
  name: string;
  score: number;
  category: string;
}

/**
 * Retrieves Maestro targeting recommendations for TikTok
 * @param brandId - ID of the brand
 */
export const getMaestroTikTokTargetingRecommendations_api = async (
  brandId: CurrentBrand['id']
): Promise<{ data: TargetingRecommendation[] | null; error: unknown | null }> => {
  try {
    const response = await Axios.get<TargetingRecommendation[]>(
      `/tiktok/${brandId}/recommendations/interests`
    );
    return { data: response.data, error: null };
  } catch (error) {
    console.error('Error fetching targeting recommendations:', error);
    Sentry.captureException(error);
    return { data: null, error };
  }
};